import React, { useState } from 'react';
import { ChevronDown, ChevronUp, ShieldCheck } from 'lucide-react';

/**
 * PriceBreakdown — collapsible transparency line under an offer.
 *
 * Frontend-only display split (backend hat authoritative Werte).
 * Formel: driverAmount = totalPrice / 1.15, fee = totalPrice - driverAmount
 *
 * Props:
 *   totalPrice: number (required)
 *   currency:   string (default '€')
 *   t:          optional translator
 *   testIdSuffix: unique suffix per offer for testids
 */
export function PriceBreakdown({ totalPrice, currency = '€', t, testIdSuffix = 'default' }) {
  const [open, setOpen] = useState(false);
  const tr = (key, fallback) => (t ? (t(key) || fallback) : fallback);

  const price = Number(totalPrice) || 0;
  // Frontend-only display split. Backend behält die Wahrheit.
  const driverAmount = price / 1.15;
  const fee = price - driverAmount;

  const fmt = (n) => n.toFixed(2).replace('.', ',');

  return (
    <div className="mt-2 border-t border-slate-100 pt-2" data-testid={`price-breakdown-${testIdSuffix}`}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        className="w-full flex items-center justify-between text-xs font-medium text-slate-600 hover:text-orange-500 transition-colors"
        data-testid={`price-breakdown-toggle-${testIdSuffix}`}
      >
        <span className="inline-flex items-center gap-1.5">
          <ShieldCheck size={12} className="text-emerald-500" />
          {tr('trust_price_details', 'Preisdetails anzeigen')}
        </span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {open && (
        <div className="mt-2 bg-slate-50 rounded-lg p-3 text-xs space-y-1.5" data-testid={`price-breakdown-content-${testIdSuffix}`}>
          <div className="flex items-center justify-between">
            <span className="text-slate-600">{tr('trust_price_driver', 'Fahrer-Angebot')}</span>
            <span className="font-medium text-slate-800">{currency}{fmt(driverAmount)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-600">{tr('trust_price_fee', '366transfer Servicegebühr (15%)')}</span>
            <span className="font-medium text-slate-800">{currency}{fmt(fee)}</span>
          </div>
          <div className="border-t border-slate-200 my-1" />
          <div className="flex items-center justify-between">
            <span className="font-semibold text-slate-800">{tr('trust_price_total', 'Gesamt')}</span>
            <span className="font-bold text-slate-900">{currency}{fmt(price)}</span>
          </div>
          <div className="mt-2 text-[11px] text-emerald-700 bg-emerald-50 rounded-md px-2 py-1.5 flex items-start gap-1.5">
            <ShieldCheck size={12} className="mt-0.5 shrink-0" />
            <span>{tr('trust_escrow_note', 'Fahrer wird erst nach Ihrer sicheren Ankunft bezahlt.')}</span>
          </div>
        </div>
      )}
    </div>
  );
}
